import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { Button, TextInput } from "@datassential/platform-ui-lib";
import { IMacKeyword } from "@models/MacDetails";
import {
    MAC_MENU_EXAMPLES_PAGE_SIZE,
    MacMenuExampleSortDirection,
    MacMenuExampleSortKey,
    useMacMenuExamples,
} from "@apiHooks/useMacMenuExamples";
import { IconOrder, IconOrderReverse } from "@icons";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { useDebouncedCallback } from "@core/hooks/useDebouncedCallback";
import useElementHeight from "@core/hooks/useElementHeight";
import MacMenuExamplesMarkedText from "../MacMenuExamplesMarkedText/MacMenuExamplesMarkedText";
import { formatMenuExamplePrice, getPriceLabel } from "./MacMenuExamples.helpers";
import "./MacMenuExamples.scss";

export const MENU_EXAMPLES_SEARCH_PLACEHOLDER = 'Find a term';
const SEARCH_DEBOUNCE_MS = 300;
// The shared table gives every cell a fixed 40px height and line-height 1 inline; these override it
// so rows grow with long items/descriptions.
const GROWING_CELL = { height: 'auto', lineHeight: 1.4 };
// Right-aligned like MT, wide enough for "Price (CAD)" plus the sort arrow on one line.
const PRICE_CELL = { flex: '0 0 160px', justifyContent: 'flex-end', textAlign: 'right' as const, paddingRight: 24 };

const SORT_ICONS: Record<MacMenuExampleSortDirection, JSX.Element> = {
    asc: <IconOrderReverse/>,
    desc: <IconOrder/>,
};

// As in MT: price reads dearest first, text columns A-Z.
function getDefaultSortDirection(sortKey: MacMenuExampleSortKey): MacMenuExampleSortDirection {
    return sortKey === 'price' ? 'desc' : 'asc';
}

interface MacMenuExamplesProps {
    macId: number;
    countryCode: string;
    keyword: IMacKeyword;
}

function MacMenuExamples({ macId, countryCode, keyword }: MacMenuExamplesProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    // The table head sticks right below the sticky section header, whose height changes when it wraps.
    const { ref: headerRef, height: headerHeight } = useElementHeight<HTMLDivElement>();
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    // The search runs on the server, so requests wait for a pause in typing.
    const applySearch = useDebouncedCallback((value: string) => setSearch(value.trim()), SEARCH_DEBOUNCE_MS);
    const [{ sortKey, direction }, setSort] = useState<{ sortKey: MacMenuExampleSortKey; direction: MacMenuExampleSortDirection }>(
        { sortKey: 'item', direction: getDefaultSortDirection('item') },
    );
    const { items, isLoading, hasMore, loadMore } = useMacMenuExamples(macId, keyword.id, { search, sortKey, direction });

    const changeQuery = (value: string) => {
        setQuery(value);
        applySearch(value);
    };

    // The sorted column flips; a newly chosen one starts at its own default direction.
    const toggleSort = useCallback((key: MacMenuExampleSortKey) => {
        setSort((current) => (current.sortKey === key
            ? { sortKey: key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
            : { sortKey: key, direction: getDefaultSortDirection(key) }));
    }, []);

    useEffect(() => {
        rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [keyword.id]);

    const tableConfig = useMemo(() => {
        const getSortProps = (key: MacMenuExampleSortKey) => ({
            isActive: sortKey === key,
            icon: sortKey === key ? SORT_ICONS[direction] : null,
            handleHeaderClick: () => toggleSort(key),
        });

        const config: iTableConfigItem[] = [
            {
                title: 'Item',
                ...getSortProps('item'),
                styles: { cell: { ...GROWING_CELL, flex: '0 0 25%' } },
                Value: ({ index }) => <MacMenuExamplesMarkedText text={items[index]?.item ?? ''}/>,
            },
            {
                title: 'Description',
                styles: { cell: { ...GROWING_CELL, flex: '1 1 auto' } },
                Value: ({ index }) => <MacMenuExamplesMarkedText text={items[index]?.description ?? ''}/>,
            },
            {
                title: getPriceLabel(countryCode),
                ...getSortProps('price'),
                styles: { cell: { ...GROWING_CELL, ...PRICE_CELL } },
                Value: ({ index }) => <>{formatMenuExamplePrice(items[index]?.price ?? '', countryCode)}</>,
            },
            {
                title: 'Venue',
                ...getSortProps('venue'),
                styles: { cell: { ...GROWING_CELL, flex: '0 0 20%' } },
                Value: ({ index }) => <>{items[index]?.venue}</>,
            },
        ];

        return config;
    }, [items, sortKey, direction, toggleSort, countryCode]);

    return (
        <div
            className="MacMenuExamples"
            ref={rootRef}
            style={{ '--menu-examples-header-height': `${headerHeight}px` } as CSSProperties}
            data-testid="blockMacDetailsMenuExamples"
        >
            <div className="MacMenuExamples__header" ref={headerRef}>
                <div className="MacMenuExamples__title" data-testid="textMacDetailsTableTitle">
                    Word: <span className="MacMenuExamples__word">{keyword.text}</span>
                </div>
                <div className="MacMenuExamples__search">
                    <AiOutlineSearch className="MacMenuExamples__search-icon" aria-hidden/>
                    <TextInput
                        value={query}
                        setValue={changeQuery}
                        placeholder={MENU_EXAMPLES_SEARCH_PLACEHOLDER}
                        className="MacMenuExamples__search-input"
                        testId="inputMacDetailsMenuExamplesSearch"
                    />
                </div>
            </div>
            <div className="MacMenuExamples__content">
                <Table
                    isCopyButtonHidden
                    hasInnerScroll={false}
                    rowHeight="auto"
                    config={tableConfig}
                    rowsCount={items.length}
                    isLoading={isLoading && items.length === 0}
                    theme="dark"
                    testId="tableMacDetailsMenuExamples"
                />
                {hasMore && (
                    <div className="MacMenuExamples__more">
                        <Button
                            onClick={loadMore}
                            isDisabled={isLoading}
                            className="MacMenuExamples__more-button"
                            testId="buttonMacDetailsMenuExamplesLoadMore"
                        >
                            {isLoading ? 'loading...' : `Load ${MAC_MENU_EXAMPLES_PAGE_SIZE} more`}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MacMenuExamples;
